import { Box, Button, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { useTheme } from "../../context/ThemeContext";
import { ZERO } from "../../shared/bigUtil";
import { CostType } from "../../shared/CaseConfiguration.ts";
import { getColorForCosts } from "../analytics/CostColors.ts";
import { CurrencyTextField } from "../general/CurrencyTextField";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { DialogShell } from "../general/DialogShell";

export const CostInputDialog = ({
  open,
  onClose,
  title,
  subTitle,
  costs,
  fixed,
  submitLabel,
}: {
  open: boolean;
  onClose: (updateCosts?: { [type in CostType]?: number }) => void;
  title: string;
  subTitle: string;
  costs: { [type in CostType]?: number };
  // what settlements dropped of each type, fixed, on top of which the
  // amounts entered come
  fixed?: { [type in CostType]?: number };
  // what saving says, where it is more than the disbursements
  submitLabel?: string;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "costInputDialog" });
  const { currency } = useCurrency();
  const [disbursements, setDisbursements] =
    useState<{ [type in CostType]?: number }>(costs);
  const total = useMemo(
    () =>
      [...Object.values(disbursements), ...Object.values(fixed ?? {})]
        .reduce((previous, current) => previous.add(current ?? 0), ZERO)
        .toNumber(),
    [disbursements, fixed],
  );

  return (
    <DialogShell
      open={open}
      onClose={() => onClose()}
      title={title}
      description={subTitle}
      actions={
        <>
          <Button onClick={() => onClose()} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            onClick={() => onClose(disbursements)}
            variant="contained"
            autoFocus
          >
            {submitLabel ?? t("save")}
          </Button>
        </>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 1.75,
        }}
      >
        {(
          Object.entries(disbursements) as [CostType, number | undefined][]
        ).map(([type, value]) => (
          <div key={type}>
            <Typography
              variant="caption"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.875,
                mb: 0.625,
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              {/* the colour the type has in the charts of the costs */}
              <Box
                component="span"
                sx={{
                  flex: "none",
                  width: 8,
                  height: 8,
                  borderRadius: 0.5,
                  bgcolor: getColorForCosts(type, theme).backgroundColor,
                }}
              />
              {t(`costTypes.${type}`)}
            </Typography>
            {(fixed?.[type] ?? 0) > 0 && (
              <Typography
                variant="caption"
                component="div"
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                {t("fixed", { amount: currency.format(fixed![type]!) })}
              </Typography>
            )}
            <CurrencyTextField
              currency={currency}
              ariaLabel={t(`costTypes.${type}`)}
              onValueChange={(newValue) => {
                const newDisbursements = { ...disbursements };
                newDisbursements[type] = newValue;
                setDisbursements(newDisbursements);
              }}
              variant="outlined"
              size="small"
              min={0}
              value={value}
              sx={{ width: "100%" }}
            />
          </div>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("totalAmount")}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}>
          {currency.format(total)}
        </Typography>
      </Box>
    </DialogShell>
  );
};
